import React, { useEffect, useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Trash2, Edit3, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Expense, Category, ResponseWarning } from '../types';
import { formatCurrency } from '../utils/formatters';
import { ExpenseTableSkeleton } from './SkeletonLoaders';

type SortField = 'date' | 'amount' | null;
type SortDirection = 'asc' | 'desc';

interface ExpenseListProps {
  expenses: Expense[];
  warnings?: ResponseWarning[];
  onDeleteExpense: (id: number) => void;
  onEditExpense?: (expense: Expense) => void;
  loading?: boolean;
  selectedMonth?: Date;
  targetCurrency?: string;
  categories?: Category[];
}

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  warnings = [],
  onDeleteExpense,
  onEditExpense,
  loading = false,
  selectedMonth,
  targetCurrency,
  categories = []
}) => {
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);

  // Get only parent categories
  const parentCategories = useMemo(() => {
    return categories.filter(cat => !cat.parent_id);
  }, [categories]);

  // Get subcategory IDs for a parent category
  const getSubcategoryIds = useMemo(() => {
    return (parentId: number): number[] => {
      return categories
        .filter(cat => cat.parent_id === parentId)
        .map(cat => cat.id);
    };
  }, [categories]);

  // Filter and sort expenses
  const filteredAndSortedExpenses = useMemo(() => {
    if (!expenses) return [];
    let result = [...expenses];

    // Filter by categories (parent + subcategories)
    if (selectedCategories.length > 0) {
      const allCategoryIds = selectedCategories.flatMap(categoryId => {
        const subcategoryIds = getSubcategoryIds(categoryId);
        return [categoryId, ...subcategoryIds];
      });

      result = result.filter(expense =>
        expense.category && allCategoryIds.includes(expense.category.id)
      );
    }

    // Sort
    if (sortField === 'date') {
      result.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    } else if (sortField === 'amount') {
      result.sort((a, b) => {
        const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
        const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
        return sortDirection === 'asc' ? amountA - amountB : amountB - amountA;
      });
    }

    return result;
  }, [expenses, selectedCategories, sortField, sortDirection, getSubcategoryIds]);

  // Calculate total for filtered expenses
  const filteredTotal = useMemo(() => {
    return filteredAndSortedExpenses.reduce((sum, expense) => {
      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount;
      return sum + amount;
    }, 0);
  }, [filteredAndSortedExpenses]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="sort-icon" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp size={14} className="sort-icon active" /> :
      <ArrowDown size={14} className="sort-icon active" />;
  };

  const getCategoryLabel = () => {
    if (selectedCategories.length === 0) {
      return '';
    }
    if (selectedCategories.length === 1) {
      const category = parentCategories.find(cat => cat.id === selectedCategories[0]);
      return category ? `: ${category.name}` : '';
    }
    return `: ${selectedCategories.length} selected`;
  };

  const toggleCategory = (categoryId: number) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
    setShowCategoryDropdown(false);
  };

  useEffect(() => {
    const calculateHeight = () => {
      const categoryWheelContainer = document.querySelector('.category-wheel-container') as HTMLElement;
      const monthlyExpensesContainer = document.querySelector('.monthly-expenses-container') as HTMLElement;

      if (categoryWheelContainer && monthlyExpensesContainer) {
        const categoryHeight = categoryWheelContainer.offsetHeight;
        monthlyExpensesContainer.style.height = `${categoryHeight}px`;
      }
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(calculateHeight);
    const categoryWheelContainer = document.querySelector('.category-wheel-container');

    if (categoryWheelContainer) {
      resizeObserver.observe(categoryWheelContainer);
    }

    window.addEventListener('resize', calculateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateHeight);
    };
  }, [expenses]);

  if (loading) {
    return <ExpenseTableSkeleton />;
  }

  if (!expenses || expenses.length === 0) {
    return (
      <div className="empty-list-message">
        No expenses found for the selected period.
      </div>
    );
  }

  return (
    <div className="expense-list-wrapper">
      {/* Warnings Display */}
      {warnings.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertTriangle size={20} color="#856404" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            {warnings.map((warning, idx) => (
              <div key={idx} style={{ marginBottom: idx < warnings.length - 1 ? '8px' : '0' }}>
                <strong style={{ color: '#856404' }}>{warning.code}:</strong>{' '}
                <span style={{ color: '#856404' }}>{warning.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Bar with Category Controls */}
      <div className="expense-filter-bar">
        {/* Category Filter Button with Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            ref={filterButtonRef}
            className="category-filter-trigger-btn"
            onClick={() => {
              setShowCategoryDropdown(!showCategoryDropdown);
            }}
          >
            <span>Filter by Category{getCategoryLabel()}</span>
            <ArrowUpDown size={16} />
          </button>

          {/* Dropdown Panel */}
          {showCategoryDropdown && (
            <>
              <div
                className="category-dropdown-backdrop"
                onClick={() => {
                  setShowCategoryDropdown(false);
                }}
              />
              <div className="category-dropdown-panel-fixed">
                <div className="category-dropdown-header">
                  <span>Filter Categories</span>
                  {selectedCategories.length > 0 && (
                    <button
                      className="clear-filters-btn"
                      onClick={clearAllCategories}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="category-dropdown-list">
                  {parentCategories.length === 0 && <div>No categories found</div>}
                  {parentCategories.map((category) => (
                    <label
                      key={category.id}
                      className="category-checkbox-item"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                      />
                      <span>{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sum display when filtered */}
        {selectedCategories.length > 0 && (
          <div className="filter-summary-inline">
            <span className="summary-count">{filteredAndSortedExpenses.length} expenses</span>
            <span className="summary-total">
              Total: {formatCurrency(filteredTotal, targetCurrency || expenses[0]?.currency || 'USD')}
            </span>
          </div>
        )}
      </div>

      <div className="expense-table-scroll">
        <table className="expenses-table">
        <thead>
          <tr>
            <th
              className="sortable-header"
              onClick={() => handleSort('date')}
              style={{ cursor: 'pointer' }}
            >
              <span className="header-content">
                Date
                {getSortIcon('date')}
              </span>
            </th>
            <th>Name</th>
            <th>Category</th>
            <th
              className="sortable-header"
              onClick={() => handleSort('amount')}
              style={{ cursor: 'pointer' }}
            >
              <span className="header-content">
                Amount
                {getSortIcon('amount')}
              </span>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedExpenses.map((expense) => (
          <tr key={expense.id}>
            <td>{format(parseISO(expense.created_at), 'MMM d')}</td>
            <td>{expense.name}</td>
            <td>
              <span className="category-badge">
                {expense.category?.name || "Uncategorized"}
              </span>
            </td>
            <td className="amount">
              {formatCurrency(expense.amount, expense.currency)}
            </td>
            <td className="actions">
              {onEditExpense && (
                <button
                  className="edit-button"
                  onClick={() => onEditExpense(expense)}
                  title="Edit expense"
                >
                  <Edit3 size={16} />
                </button>
              )}
              <button
                className="delete-button"
                onClick={() => onDeleteExpense(expense.id)}
                title="Delete expense"
              >
                <Trash2 size={16} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
      </div>
    </div>
  );
};

export default ExpenseList;
